import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { injectIntl, defineMessages } from 'react-intl';
import prettyBytes from 'pretty-bytes';
import { Button } from 'primereact/button';
import { intlShape } from '../../components/prop-types';

const messages = defineMessages({
  dropFiles: {
    id: 'logoUpload.drag-and-drop-message',
    defaultMessage: 'Drag and drop a logo image here, or click to select one',
  },
  upload: { id: 'actions.upload', defaultMessage: 'Upload' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

function LogoUploadDropzone(props) {
  const maxSize = 2097152;

  const {
    intl: { formatMessage, locale },
    onUpload,
    onCancel,
    onAcceptedFiles,
  } = props;

  const {
    acceptedFiles, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject,
  } = useDropzone({
    onDropAccepted: onAcceptedFiles,
    multiple: false,
    accept: ['image/jpg', 'image/png'],
    minSize: 0,
    maxSize,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragAccept, isDragReject],
  );

  const files = acceptedFiles.map(file => (
    <div
      key={file.path}
      style={{
        border: '1px dashed green',
        backgroundColor: 'rgba(180,250,147,1)',
      }}
    >
      {file.path}
      {' - '}
      {prettyBytes(file.size, { locale })}
    </div>
  ));

  return (
    <section className="container">
      <div {...getRootProps({ className: 'dropzone' })} style={style}>
        <input {...getInputProps()} />
        <p>{formatMessage(messages.dropFiles)}</p>
      </div>
      <aside style={{ marginBottom: '10px' }}>
        {files}
      </aside>
      <Button
        type="button"
        label={formatMessage(messages.upload)}
        disabled={acceptedFiles.length === 0}
        onClick={() => {
          onUpload(acceptedFiles);
          acceptedFiles.length = 0;
        }}
      />
      {onCancel && (
        <Button
          label={formatMessage(messages.cancel)}
          className="p-button-secondary"
          onClick={onCancel}
        />
      )}
    </section>
  );
}

LogoUploadDropzone.propTypes = {
  intl: intlShape.isRequired,
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onAcceptedFiles: PropTypes.func,
};

LogoUploadDropzone.defaultProps = {
  onCancel: null,
  onAcceptedFiles: null,
};

export default injectIntl(LogoUploadDropzone);
